import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { UsersAPI, RolesAPI, ContentsAPI, UploadsAPI, UploadGroupsAPI, S3API, MasterAPI, uploadFileToS3 } from '../api/api';
import Navigation from '../components/Navigation';
import ConfirmModal from '../components/ConfirmModal';
import LucideIconPicker from '../components/LucideIconPicker';
import * as pdfjsLib from 'pdfjs-dist';
import './AdminPage.css';

// PDF.js Worker 설정
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

function AdminPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { success, error: showError, warning, info } = useAlert();
    const [activeTab, setActiveTab] = useState('users');

    // Users state
    const [users, setUsers] = useState([]);
    const [userFilter, setUserFilter] = useState('all');
    const [usersLoading, setUsersLoading] = useState(false);

    // Roles state
    const [roles, setRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [roleForm, setRoleForm] = useState({ name: '', description: '', level: 0 });

    // Permissions state
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [menuStructure, setMenuStructure] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [permissionsLoading, setPermissionsLoading] = useState(false);

    // Modal states
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectUserId, setRejectUserId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectReasonType, setRejectReasonType] = useState('');
    const [showUserEditModal, setShowUserEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showRoleAssignModal, setShowRoleAssignModal] = useState(false);
    const [assigningUser, setAssigningUser] = useState(null);
    const [pendingRoleId, setPendingRoleId] = useState(null);

    // Master data state (소속/본부)
    const [affiliations, setAffiliations] = useState([]);
    const [headquarters, setHeadquarters] = useState([]);

    // Files state
    const [selectedItem, setSelectedItem] = useState({ type: '', id: '', name: '' });
    const [expandedSections, setExpandedSections] = useState({});
    const [expandedCategories, setExpandedCategories] = useState({});
    const [uploads, setUploads] = useState([]);
    const [uploadsLoading, setUploadsLoading] = useState(false);

    // Upload Groups state (자료 그룹)
    const [uploadGroups, setUploadGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [fileViewMode, setFileViewMode] = useState('groups'); // 'groups' | 'files'

    // Group edit modal state
    const [showGroupEditModal, setShowGroupEditModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [groupEditForm, setGroupEditForm] = useState({ title: '', description: '' });

    // Upload state
    const [uploadProgress, setUploadProgress] = useState({});
    const [isUploading, setIsUploading] = useState(false);

    // Preview modal state
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    // Upload modal state
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadModalMode, setUploadModalMode] = useState('addFiles'); // 'createGroup' | 'addFiles'
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploadTitle, setUploadTitle] = useState('');
    const fileInputRef = useRef(null);

    // Drag and drop state for file ordering
    const [isDragOver, setIsDragOver] = useState(false);
    const [draggedFileIndex, setDraggedFileIndex] = useState(null);
    const [draggedUploadIndex, setDraggedUploadIndex] = useState(null);

    // Hover preview state
    const [hoverPreviewUpload, setHoverPreviewUpload] = useState(null);
    const [hoverPreviewUrl, setHoverPreviewUrl] = useState('');
    const hoverPreviewPositionRef = useRef({ x: 0, y: 0 });
    const previewUrlCache = useRef({});
    const hoverTimeoutRef = useRef(null);
    const currentHoverUploadIdRef = useRef(null);

    // Section management state
    const [sectionMgmtData, setSectionMgmtData] = useState([]);
    const [sectionMgmtLoading, setSectionMgmtLoading] = useState(false);
    const [selectedMgmtItem, setSelectedMgmtItem] = useState(null);
    const [editMode, setEditMode] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [expandedMgmtSections, setExpandedMgmtSections] = useState({});
    const [expandedMgmtCategories, setExpandedMgmtCategories] = useState({});

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        confirmText: '확인',
        cancelText: '취소',
        onConfirm: null
    });

    // 반려 사유 옵션
    const rejectReasonOptions = [
        { value: 'invalid_info', label: '정보 불일치' },
        { value: 'duplicate', label: '중복 가입' },
        { value: 'unauthorized', label: '승인 불가' },
        { value: 'incomplete', label: '정보 미비' },
        { value: 'policy', label: '정책 위반' },
        { value: 'other', label: '기타' },
    ];

    // 관리자 권한 확인
    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

    // 사용자 목록 조회
    const fetchUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const params = {};
            if (userFilter !== 'all') {
                params.status = userFilter;
            }
            const response = await UsersAPI.getUsers(params);
            setUsers(response.data || response.users || []);
        } catch (err) {
            console.error('Failed to fetch users:', err);
            showError('사용자 목록을 불러오는데 실패했습니다.');
        } finally {
            setUsersLoading(false);
        }
    }, [userFilter, showError]);

    // 역할 목록 조회
    const fetchRoles = useCallback(async () => {
        setRolesLoading(true);
        try {
            const response = await RolesAPI.getRoles();
            setRoles(response.data || response.roles || []);
        } catch (err) {
            console.error('Failed to fetch roles:', err);
            showError('역할 목록을 불러오는데 실패했습니다.');
        } finally {
            setRolesLoading(false);
        }
    }, [showError]);

    // 메뉴 구조 조회
    const fetchMenuStructure = useCallback(async () => {
        try {
            const response = await ContentsAPI.getMenu();
            setMenuStructure(response.data?.sections || response.data || []);
        } catch (error) {
            console.error('Failed to fetch menu structure:', error);
        }
    }, []);

    // 소속/본부 마스터 데이터 조회
    const fetchMasterData = useCallback(async () => {
        try {
            const [affRes, hqRes] = await Promise.all([
                MasterAPI.getAffiliations(),
                MasterAPI.getHeadquarters()
            ]);
            setAffiliations(affRes.data || []);
            setHeadquarters(hqRes.data || []);
        } catch (error) {
            console.error('Failed to fetch master data:', error);
        }
    }, []);

    // 역할 권한 조회
    const fetchRolePermissions = useCallback(async (roleId) => {
        if (!roleId) return;
        setPermissionsLoading(true);
        try {
            const response = await RolesAPI.getRolePermissions(roleId);
            const data = response.data || response;
            const permMap = {};

            // 새로운 응답 형식: sections > categories > parts 계층 구조
            if (data.sections) {
                // 메뉴 구조도 함께 설정
                setMenuStructure(data.sections);

                data.sections.forEach(section => {
                    permMap[`section_${section.id}`] = { canAccess: section.canAccess };

                    section.categories?.forEach(category => {
                        permMap[`category_${category.id}`] = { canAccess: category.canAccess };

                        category.parts?.forEach(part => {
                            permMap[`part_${part.id}`] = {
                                canView: part.canView,
                                canCreate: part.canCreate,
                                canEdit: part.canEdit,
                                canDelete: part.canDelete,
                                canUpload: part.canUpload,
                            };
                        });
                    });
                });
            }
            setPermissions(permMap);
        } catch (error) {
            console.error('Failed to fetch role permissions:', error);
            setPermissions({});
        } finally {
            setPermissionsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAdmin) {
            navigate('/main');
            return;
        }
        fetchUsers();
        fetchRoles();
        fetchMenuStructure();
        fetchMasterData();
    }, [isAdmin, navigate, fetchUsers, fetchRoles, fetchMenuStructure, fetchMasterData]);

    useEffect(() => {
        if (selectedRoleId) {
            fetchRolePermissions(selectedRoleId);
        }
    }, [selectedRoleId, fetchRolePermissions]);

    // 권한 관리 탭에서 첫 번째 역할 자동 선택
    useEffect(() => {
        if (activeTab === 'permissions' && roles.length > 0 && !selectedRoleId) {
            setSelectedRoleId(roles[0].id);
        }
    }, [activeTab, roles, selectedRoleId]);

    // 섹션 관리 데이터 로드
    const fetchSectionMgmtData = useCallback(async () => {
        setSectionMgmtLoading(true);
        try {
            const response = await ContentsAPI.getMenu({ silent: true, includeEmpty: true });
            setSectionMgmtData(response.data?.sections || []);
        } catch (err) {
            console.error('Failed to fetch section management data:', err);
            showError('콘텐츠 구조를 불러오는데 실패했습니다.');
        } finally {
            setSectionMgmtLoading(false);
        }
    }, [showError]);

    // 섹션 관리 / 파일 관리 / 권한 관리 탭 진입 시 데이터 로드
    useEffect(() => {
        if (activeTab === 'sections') {
            fetchSectionMgmtData();
        }
        if (activeTab === 'files') {
            fetchMenuStructure();
        }
        if (activeTab === 'permissions' && selectedRoleId) {
            fetchRolePermissions(selectedRoleId);
        }
    }, [activeTab, fetchSectionMgmtData, fetchMenuStructure]); // selectedRoleId, fetchRolePermissions는 별도 useEffect에서 처리

    // 섹션 관리 - 토글
    const toggleMgmtSection = (sectionId) => {
        setExpandedMgmtSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
    };
    const toggleMgmtCategory = (categoryId) => {
        setExpandedMgmtCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
    };

    // 섹션 관리 - 카테고리 CRUD
    const handleCreateCategory = (sectionId) => {
        setEditMode('create-category');
        setEditForm({ sectionId, name: '' });
        setSelectedMgmtItem(null);
    };

    const handleEditCategory = (category) => {
        setEditMode('edit-category');
        setEditForm({
            id: category.id,
            name: category.name,
            displayOrder: category.displayOrder,
        });
        setSelectedMgmtItem({ type: 'category', data: category });
    };

    const handleSaveCategory = async () => {
        if (!editForm.name?.trim()) {
            warning('카테고리 이름을 입력해주세요.');
            return;
        }
        try {
            if (editMode === 'create-category') {
                await ContentsAPI.createCategory({
                    sectionId: editForm.sectionId,
                    name: editForm.name.trim(),
                });
                success('카테고리가 생성되었습니다.');
            } else {
                await ContentsAPI.updateCategory(editForm.id, {
                    name: editForm.name.trim(),
                    displayOrder: editForm.displayOrder,
                });
                success('카테고리가 수정되었습니다.');
            }
            setEditMode(null);
            setEditForm({});
            setSelectedMgmtItem(null);
            fetchSectionMgmtData();
        } catch (err) {
            showError(err.message || '저장에 실패했습니다.');
        }
    };

    const handleDeleteCategory = (category) => {
        setConfirmModal({
            isOpen: true,
            title: '카테고리 삭제',
            message: `"${category.name}" 카테고리를 삭제하시겠습니까?\n하위 파트와 업로드된 파일도 모두 삭제됩니다.`,
            type: 'danger',
            confirmText: '삭제',
            cancelText: '취소',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await ContentsAPI.deleteCategory(category.id);
                    success('카테고리가 삭제되었습니다.');
                    setEditMode(null);
                    setSelectedMgmtItem(null);
                    fetchSectionMgmtData();
                } catch (err) {
                    showError(err.message || '삭제에 실패했습니다.');
                }
            },
        });
    };

    // 섹션 관리 - 파트 CRUD
    const handleCreatePart = (categoryId) => {
        setEditMode('create-part');
        setEditForm({
            categoryId,
            name: '',
            icon: '',
            externalUrl: '',
            partType: 'LINK',
            buttonText: '바로가기',
        });
        setSelectedMgmtItem(null);
    };

    const handleEditPart = (part) => {
        setEditMode('edit-part');
        setEditForm({
            id: part.id,
            name: part.name,
            icon: part.icon || '',
            displayOrder: part.displayOrder,
            externalUrl: part.externalUrl || '',
            partType: part.partType || 'LINK',
            buttonText: part.buttonText || '바로가기',
        });
        setSelectedMgmtItem({ type: 'part', data: part });
    };

    const handleSavePart = async () => {
        if (!editForm.name?.trim()) {
            warning('파트 이름을 입력해주세요.');
            return;
        }
        if ((editForm.partType || 'LINK') === 'LINK' && !editForm.externalUrl?.trim()) {
            warning('외부 URL을 입력해주세요.');
            return;
        }
        if (!editForm.buttonText?.trim()) {
            warning('버튼 텍스트를 입력해주세요.');
            return;
        }
        if (!editForm.icon?.trim()) {
            warning('아이콘을 선택해주세요.');
            return;
        }
        try {
            if (editMode === 'create-part') {
                await ContentsAPI.createPart({
                    categoryId: editForm.categoryId,
                    name: editForm.name.trim(),
                    icon: editForm.icon?.trim() || undefined,
                    externalUrl: editForm.externalUrl?.trim() || undefined,
                    partType: editForm.partType,
                    buttonText: editForm.buttonText?.trim() || undefined,
                });
                success('파트가 생성되었습니다.');
            } else {
                await ContentsAPI.updatePart(editForm.id, {
                    name: editForm.name.trim(),
                    icon: editForm.icon?.trim() || undefined,
                    displayOrder: editForm.displayOrder,
                    externalUrl: editForm.externalUrl?.trim() || undefined,
                    partType: editForm.partType,
                    buttonText: editForm.buttonText?.trim() || undefined,
                });
                success('파트가 수정되었습니다.');
            }
            setEditMode(null);
            setEditForm({});
            setSelectedMgmtItem(null);
            fetchSectionMgmtData();
        } catch (err) {
            showError(err.message || '저장에 실패했습니다.');
        }
    };

    const handleDeletePart = (part) => {
        setConfirmModal({
            isOpen: true,
            title: '파트 삭제',
            message: `"${part.name}" 파트를 삭제하시겠습니까?\n업로드된 파일도 모두 삭제됩니다.`,
            type: 'danger',
            confirmText: '삭제',
            cancelText: '취소',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await ContentsAPI.deletePart(part.id);
                    success('파트가 삭제되었습니다.');
                    setEditMode(null);
                    setSelectedMgmtItem(null);
                    fetchSectionMgmtData();
                } catch (err) {
                    showError(err.message || '삭제에 실패했습니다.');
                }
            },
        });
    };

    // 회원 승인
    const handleApprove = (userId) => {
        setConfirmModal({
            isOpen: true,
            title: '회원 승인',
            message: '이 회원을 승인하시겠습니까?',
            type: 'info',
            confirmText: '승인',
            cancelText: '취소',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await UsersAPI.approveUser(userId);
                    success('회원이 승인되었습니다.');
                    fetchUsers();
                } catch (err) {
                    showError(err.message || '승인 처리에 실패했습니다.');
                }
            }
        });
    };

    // 회원 반려
    const handleReject = async () => {
        if (!rejectReason.trim()) {
            warning('반려 사유를 입력해주세요.');
            return;
        }
        try {
            await UsersAPI.rejectUser(rejectUserId, rejectReason);
            success('회원이 반려되었습니다.');
            setShowRejectModal(false);
            setRejectUserId(null);
            setRejectReason('');
            fetchUsers();
        } catch (err) {
            showError(err.message || '반려 처리에 실패했습니다.');
        }
    };

    // 회원 삭제
    const handleDeleteUser = (userId) => {
        setConfirmModal({
            isOpen: true,
            title: '회원 삭제',
            message: '정말로 이 회원을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.',
            type: 'danger',
            confirmText: '삭제',
            cancelText: '취소',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await UsersAPI.deleteUser(userId);
                    success('회원이 삭제되었습니다.');
                    fetchUsers();
                } catch (err) {
                    showError(err.message || '삭제 처리에 실패했습니다.');
                }
            }
        });
    };

    // 회원 정보 수정
    const handleUserEdit = async () => {
        try {
            await UsersAPI.updateUser(editingUser.id, {
                name: editingUser.name,
                affiliationId: editingUser.affiliationId || null,
                headquartersId: editingUser.headquartersId || null,
            });
            success('회원 정보가 수정되었습니다.');
            setShowUserEditModal(false);
            setEditingUser(null);
            fetchUsers();
        } catch (err) {
            showError(err.message || '수정에 실패했습니다.');
        }
    };

    // 역할 저장 (생성/수정)
    const handleRoleSave = async () => {
        if (!roleForm.name.trim()) {
            warning('역할명을 입력해주세요.');
            return;
        }
        try {
            if (editingRole) {
                await RolesAPI.updateRole(editingRole.id, roleForm);
                success('역할이 수정되었습니다.');
            } else {
                await RolesAPI.createRole(roleForm);
                success('역할이 생성되었습니다.');
            }
            setShowRoleModal(false);
            setEditingRole(null);
            setRoleForm({ name: '', description: '', level: 0 });
            fetchRoles();
        } catch (err) {
            showError(err.message || '저장에 실패했습니다.');
        }
    };

    // 역할 삭제
    const handleRoleDelete = (roleId) => {
        setConfirmModal({
            isOpen: true,
            title: '역할 삭제',
            message: '정말로 이 역할을 삭제하시겠습니까?',
            type: 'danger',
            confirmText: '삭제',
            cancelText: '취소',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await RolesAPI.deleteRole(roleId);
                    success('역할이 삭제되었습니다.');
                    fetchRoles();
                } catch (err) {
                    showError(err.message || '삭제에 실패했습니다.');
                }
            }
        });
    };

    // 역할 변경 확인
    const handleConfirmRoleChange = async () => {
        // 변경 없으면 그냥 닫기
        if (!pendingRoleId || pendingRoleId === assigningUser.roleId) {
            setShowRoleAssignModal(false);
            setAssigningUser(null);
            setPendingRoleId(null);
            return;
        }

        try {
            await RolesAPI.assignRole(assigningUser.id, pendingRoleId);
            success('역할이 변경되었습니다.');
            setShowRoleAssignModal(false);
            setAssigningUser(null);
            setPendingRoleId(null);
            fetchUsers();
        } catch (err) {
            showError(err.message || '역할 변경에 실패했습니다.');
        }
    };

    // 역할 해제
    const handleRevokeRole = (userId, roleId) => {
        setConfirmModal({
            isOpen: true,
            title: '역할 해제',
            message: '이 사용자의 역할을 해제하시겠습니까?',
            type: 'warning',
            confirmText: '해제',
            cancelText: '취소',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await RolesAPI.revokeRole(userId, roleId);
                    success('역할이 해제되었습니다.');
                    fetchUsers();
                } catch (err) {
                    showError(err.message || '역할 해제에 실패했습니다.');
                }
            }
        });
    };

    // 권한 변경
    const handlePermissionChange = async (type, id, permType, value) => {
        const key = `${type}_${id}`;
        const currentPerm = permissions[key] || {};
        const newPerm = { ...currentPerm, [permType]: value };

        try {
            if (type === 'section') {
                await RolesAPI.setSectionPermission(selectedRoleId, id, newPerm.canAccess);

                // 접근 해제 시 하위 권한 초기화
                if (!value) {
                    const section = menuStructure.find(s => s.id === id);
                    if (section) {
                        const resetPerm = {
                            canView: false,
                            canCreate: false,
                            canEdit: false,
                            canDelete: false,
                            canUpload: false,
                        };
                        const newPermissions = { ...permissions, [key]: newPerm };

                        for (const category of section.categories || []) {
                            for (const part of category.parts || []) {
                                await RolesAPI.setPartPermission(selectedRoleId, part.id, resetPerm);
                                newPermissions[`part_${part.id}`] = resetPerm;
                            }
                        }
                        setPermissions(newPermissions);
                        return;
                    }
                }
            } else if (type === 'category') {
                await RolesAPI.setCategoryPermission(selectedRoleId, id, newPerm.canAccess);
            } else if (type === 'part') {
                await RolesAPI.setPartPermission(selectedRoleId, id, newPerm);
            }
            setPermissions(prev => ({ ...prev, [key]: newPerm }));
        } catch (err) {
            showError(err.message || '권한 설정에 실패했습니다.');
        }
    };

    // 카테고리 전체 권한 체크 여부 확인
    const isCategoryAllChecked = (category) => {
        if (!category.parts || category.parts.length === 0) return false;
        return category.parts.every(part => {
            const perm = permissions[`part_${part.id}`];
            return perm?.canView;
        });
    };

    // 카테고리 전체 권한 변경 (읽기 권한만 설정)
    const handleCategoryAllChange = async (category, checked) => {
        const allPerm = {
            canView: checked,
            canCreate: false,
            canEdit: false,
            canDelete: false,
            canUpload: false,
        };

        try {
            // 모든 파트에 대해 권한 설정
            for (const part of category.parts || []) {
                await RolesAPI.setPartPermission(selectedRoleId, part.id, allPerm);
            }
            // 로컬 상태 업데이트
            const newPermissions = { ...permissions };
            for (const part of category.parts || []) {
                newPermissions[`part_${part.id}`] = allPerm;
            }
            setPermissions(newPermissions);
        } catch (err) {
            showError(err.message || '권한 설정에 실패했습니다.');
        }
    };

    // ============ 파일 관리 함수 ============

    // 파일 크기 포맷
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // 파일 아이콘 가져오기
    const getFileIcon = (fileType) => {
        switch (fileType) {
            case 'PDF': return '📄';
            case 'IMAGE': return '🖼️';
            default: return '📁';
        }
    };

    // 파일 아이콘 클래스 가져오기
    const getFileIconClass = (fileType) => {
        switch (fileType) {
            case 'PDF': return 'pdf';
            case 'IMAGE': return 'image';
            default: return 'other';
        }
    };

    // 섹션 토글 (아코디언)
    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    // 카테고리 토글 (아코디언)
    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    // 아이템 선택 핸들러 (섹션/카테고리/파트)
    const handleSelectItem = async (type, id, name, partId = null) => {
        setSelectedItem({ type, id, name });
        setUploads([]);
        setUploadGroups([]);
        setSelectedGroup(null);
        setFileViewMode('groups');

        // 파트가 있으면 파트의 자료 그룹 조회
        if (type === 'part' && id) {
            await fetchUploadGroups(id);
        }
    };

    // 자료 그룹 목록 조회
    const fetchUploadGroups = async (partId) => {
        if (!partId) return;
        setGroupsLoading(true);
        try {
            const response = await UploadGroupsAPI.getByPartId(partId);
            setUploadGroups(response.data?.groups || response.groups || []);
        } catch (error) {
            console.error('Failed to fetch upload groups:', error);
            setUploadGroups([]);
        } finally {
            setGroupsLoading(false);
        }
    };

    // 자료 그룹 선택 핸들러
    const handleSelectGroup = async (group) => {
        setSelectedGroup(group);
        setFileViewMode('files');
        await fetchUploadsByGroup(group.id);
    };

    // 그룹 목록으로 돌아가기
    const handleBackToGroups = () => {
        setSelectedGroup(null);
        setFileViewMode('groups');
        setUploads([]);
    };

    // 그룹별 업로드 목록 조회
    const fetchUploadsByGroup = async (groupId) => {
        if (!groupId) return;
        setUploadsLoading(true);
        try {
            const response = await UploadsAPI.getUploadsByGroupId(groupId);
            setUploads(response.data?.uploads || response.uploads || []);
        } catch (error) {
            console.error('Failed to fetch uploads:', error);
            setUploads([]);
        } finally {
            setUploadsLoading(false);
        }
    };

    // 업로드 목록 조회 (하위 호환을 위해 유지)
    const fetchUploads = async (partId) => {
        if (!partId) return;
        setUploadsLoading(true);
        try {
            const response = await UploadsAPI.getUploadsByPartId(partId);
            setUploads(response.data?.uploads || response.uploads || []);
        } catch (error) {
            console.error('Failed to fetch uploads:', error);
            setUploads([]);
        } finally {
            setUploadsLoading(false);
        }
    };

    // 파일 선택 버튼 클릭 핸들러
    const handleFileButtonClick = () => {
        fileInputRef.current?.click();
    };

    // 확장자 기반 MIME 타입 폴백
    const getMimeType = (file) => {
        if (file.type) return file.type;
        const ext = file.name.split('.').pop()?.toLowerCase();
        const mimeMap = {
            pdf: 'application/pdf',
            mp4: 'video/mp4',
            webm: 'video/webm',
            mov: 'video/quicktime',
        };
        return mimeMap[ext] || 'application/octet-stream';
    };

    // 파일 유효성 검사 및 추가 공통 함수
    const validateAndSetFiles = (files) => {
        const fileSizeLimits = {
            pdf: 500 * 1024 * 1024,      // 500MB
            video: 700 * 1024 * 1024,    // 700MB
        };

        const getFileCategory = (file) => {
            const mimeType = getMimeType(file);
            if (mimeType === 'application/pdf') return 'pdf';
            if (mimeType.startsWith('video/')) return 'video';
            return null;
        };

        const categoryNames = {
            pdf: 'PDF',
            video: '동영상',
        };

        for (const file of files) {
            const category = getFileCategory(file);

            if (!category) {
                warning(`허용되지 않는 파일 형식입니다: ${file.name}\n허용 형식: PDF, 동영상(MP4, WebM, MOV)`);
                return;
            }

            const maxSize = fileSizeLimits[category];
            if (file.size > maxSize) {
                const maxSizeMB = maxSize / (1024 * 1024);
                warning(`${categoryNames[category]} 파일은 ${maxSizeMB}MB를 초과할 수 없습니다: ${file.name}`);
                return;
            }
        }

        setUploadFiles(prev => [...prev, ...files]);
    };

    // 파일 선택 핸들러 - 모달 내에서 파일 선택
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        validateAndSetFiles(files);
        e.target.value = '';
    };

    // 드롭존 드래그 핸들러
    const handleDropZoneDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDropZoneDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDropZoneDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) validateAndSetFiles(files);
    };

    // 업로드 모달 확인 핸들러
    const handleUploadConfirm = async () => {
        if (!uploadTitle.trim()) {
            warning('제목을 입력해주세요.');
            return;
        }

        if (uploadModalMode === 'createGroup') {
            // 그룹만 생성 (파일 없이)
            try {
                setShowUploadModal(false);
                await UploadGroupsAPI.create({
                    partId: selectedItem.id,
                    title: uploadTitle.trim(),
                });
                success('파일 그룹이 생성되었습니다.');
                if (selectedItem.type === 'part' && selectedItem.id) {
                    await fetchUploadGroups(selectedItem.id);
                }
            } catch (err) {
                showError(err.message || '그룹 생성에 실패했습니다.');
            }
            return;
        }

        // 파일 추가 모드
        if (uploadFiles.length === 0) {
            warning('파일을 선택해주세요.');
            return;
        }
        setShowUploadModal(false);
        handleFileUpload(uploadFiles, uploadTitle);
    };

    // 업로드 모달 취소 핸들러
    const handleUploadCancel = () => {
        setShowUploadModal(false);
        setUploadFiles([]);
        setUploadTitle('');
        setDraggedFileIndex(null);
    };

    // 업로드 모달 내 파일 드래그 핸들러
    const handleFileDragStart = (e, index) => {
        setDraggedFileIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleFileDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleFileDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedFileIndex === null || draggedFileIndex === dropIndex) return;

        const newFiles = [...uploadFiles];
        const [draggedFile] = newFiles.splice(draggedFileIndex, 1);
        newFiles.splice(dropIndex, 0, draggedFile);
        setUploadFiles(newFiles);
        setDraggedFileIndex(null);
    };

    const handleFileDragEnd = () => {
        setDraggedFileIndex(null);
    };

    // 기존 파일 목록 드래그 핸들러
    const handleUploadDragStart = (e, index) => {
        // 드래그 시작 시 호버 프리뷰 비활성화
        setHoverPreviewUpload(null);
        setHoverPreviewUrl('');
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }

        setDraggedUploadIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleUploadDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleUploadDrop = async (e, dropIndex) => {
        e.preventDefault();
        if (draggedUploadIndex === null || draggedUploadIndex === dropIndex) return;

        const newUploads = [...uploads];
        const [draggedUpload] = newUploads.splice(draggedUploadIndex, 1);
        newUploads.splice(dropIndex, 0, draggedUpload);
        setUploads(newUploads);
        setDraggedUploadIndex(null);

        // 서버에 순서 저장
        try {
            const orders = newUploads.map((upload, index) => ({
                id: upload.id,
                displayOrder: index + 1,
            }));
            await UploadsAPI.updateDisplayOrders(orders);
        } catch (error) {
            console.error('Failed to update display orders:', error);
            // 실패 시 원래 목록으로 복구
            if (selectedGroup) {
                fetchUploadsByGroup(selectedGroup.id);
            }
        }
    };

    const handleUploadDragEnd = () => {
        setDraggedUploadIndex(null);
    };

    // 파일 목록 영역에서 마우스 이동 감지 (호버 미리보기)
    useEffect(() => {
        const handleMouseMove = (e) => {
            // 드래그 중이면 무시
            if (draggedUploadIndex !== null) return;

            // 현재 마우스 아래 요소 찾기
            const element = document.elementFromPoint(e.clientX, e.clientY);
            const row = element?.closest('tr[data-upload-id]');

            if (row) {
                const uploadId = row.dataset.uploadId;

                // 이미 같은 파일이 호버 중이면 무시
                if (currentHoverUploadIdRef.current === uploadId) return;

                // 이전 타임아웃 클리어
                if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                }

                // 해당 upload 찾기
                const upload = uploads.find(u => u.id === uploadId);
                if (!upload || (upload.fileType !== 'PDF' && upload.fileType !== 'IMAGE')) {
                    // 미리보기 불가능한 파일이면 툴팁 숨김
                    if (currentHoverUploadIdRef.current) {
                        currentHoverUploadIdRef.current = null;
                        setHoverPreviewUpload(null);
                        setHoverPreviewUrl('');
                    }
                    return;
                }

                // 위치 계산 - 마우스 커서 바로 위
                const tooltipWidth = 320;
                const tooltipHeight = 250;
                let x = e.clientX - (tooltipWidth / 2);  // 마우스 중앙 정렬
                let y = e.clientY - tooltipHeight - 16;   // 마우스 위 16px

                // 화면 좌측 경계 체크
                if (x < 16) {
                    x = 16;
                }
                // 화면 우측 경계 체크
                if (x + tooltipWidth > window.innerWidth - 16) {
                    x = window.innerWidth - tooltipWidth - 16;
                }
                // 화면 상단 경계 체크 (위에 공간 없으면 아래로)
                if (y < 16) {
                    y = e.clientY + 16;
                }

                hoverPreviewPositionRef.current = { x, y };
                currentHoverUploadIdRef.current = uploadId;

                // 100ms 디바운스 후 툴팁 표시
                hoverTimeoutRef.current = setTimeout(() => {
                    if (currentHoverUploadIdRef.current !== uploadId) return;

                    setHoverPreviewUpload(upload);

                    // URL 로드
                    const cached = previewUrlCache.current[uploadId];
                    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
                        setHoverPreviewUrl(cached.url);
                        return;
                    }

                    S3API.getDownloadUrl(uploadId, 'VIEW')
                        .then(async response => {
                            const url = response.data?.url || response.url;

                            // PDF인 경우 첫 페이지를 이미지로 렌더링
                            if (upload.fileType === 'PDF') {
                                try {
                                    const pdf = await pdfjsLib.getDocument(url).promise;
                                    const page = await pdf.getPage(1);

                                    const scale = 1.5;
                                    const viewport = page.getViewport({ scale });

                                    const canvas = document.createElement('canvas');
                                    canvas.width = viewport.width;
                                    canvas.height = viewport.height;

                                    const context = canvas.getContext('2d');
                                    await page.render({ canvasContext: context, viewport }).promise;

                                    const imageDataUrl = canvas.toDataURL('image/png');
                                    previewUrlCache.current[uploadId] = { url: imageDataUrl, timestamp: Date.now() };

                                    if (currentHoverUploadIdRef.current === uploadId) {
                                        setHoverPreviewUrl(imageDataUrl);
                                    }
                                } catch (pdfErr) {
                                    console.error('PDF render error:', pdfErr);
                                    // 실패 시 원본 URL 사용
                                    previewUrlCache.current[uploadId] = { url, timestamp: Date.now() };
                                    if (currentHoverUploadIdRef.current === uploadId) {
                                        setHoverPreviewUrl(url);
                                    }
                                }
                            } else {
                                previewUrlCache.current[uploadId] = { url, timestamp: Date.now() };
                                if (currentHoverUploadIdRef.current === uploadId) {
                                    setHoverPreviewUrl(url);
                                }
                            }
                        })
                        .catch(err => console.error('Failed to load preview:', err));
                }, 100);
            } else {
                // 테이블 행 밖으로 나감
                if (currentHoverUploadIdRef.current) {
                    if (hoverTimeoutRef.current) {
                        clearTimeout(hoverTimeoutRef.current);
                    }
                    currentHoverUploadIdRef.current = null;
                    setHoverPreviewUpload(null);
                    setHoverPreviewUrl('');
                }
            }
        };

        // 파일 관리 탭이 활성화되고 파일 목록이 있을 때만 리스너 추가
        if (activeTab === 'files' && uploads.length > 0) {
            document.addEventListener('mousemove', handleMouseMove);
            return () => document.removeEventListener('mousemove', handleMouseMove);
        }
    }, [activeTab, uploads, draggedUploadIndex]);

    // 파일 업로드 처리
    const handleFileUpload = async (files, title) => {
        if (selectedItem.type !== 'part' || !selectedItem.id || files.length === 0) {
            warning('파트를 선택해주세요.');
            return;
        }

        setIsUploading(true);
        setUploadProgress({});

        // 그룹 ID 결정: 기존 그룹에 추가하거나 새 그룹 생성
        let groupId = selectedGroup?.id || null;

        if (!groupId) {
            // 새 자료 추가: 그룹 생성
            try {
                const groupResponse = await UploadGroupsAPI.create({
                    partId: selectedItem.id,
                    title: title,
                });
                groupId = groupResponse.data?.groupId || groupResponse.groupId;
            } catch (err) {
                console.error('Failed to create upload group:', err);
                showError('자료 그룹 생성에 실패했습니다.');
                setIsUploading(false);
                return;
            }
        }

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            try {
                // 1. 업로드 메타데이터 생성 (PENDING) - 그룹 ID 및 순서 포함
                const createResponse = await UploadsAPI.createUpload({
                    partId: selectedItem.id,
                    title: title,
                    originalName: file.name,
                    fileSize: file.size,
                    mimeType: getMimeType(file),
                    fileExtension: file.name.split('.').pop(),
                    uploadGroupId: groupId,
                    displayOrder: i + 1,  // 배열 순서대로 1, 2, 3...
                });

                const uploadId = createResponse.data?.uploadId || createResponse.uploadId;

                setUploadProgress(prev => ({
                    ...prev,
                    [file.name]: { status: 'uploading', progress: 0 }
                }));

                // 2. Presigned URL 발급
                const urlResponse = await S3API.getUploadUrl(uploadId, getMimeType(file));
                const presignedUrl = urlResponse.data?.url || urlResponse.url;
                const s3Key = urlResponse.data?.s3Key || urlResponse.s3Key;

                // 3. S3 직접 업로드 (진행률 표시)
                await uploadFileToS3(presignedUrl, file, (progress) => {
                    setUploadProgress(prev => ({
                        ...prev,
                        [file.name]: { status: 'uploading', progress }
                    }));
                });

                // 4. 업로드 완료 처리 (COMPLETED)
                const s3Config = await S3API.getConfig();
                await UploadsAPI.completeUpload({
                    uploadId,
                    s3Bucket: s3Config.data?.bucket || s3Config.bucket,
                    s3Key: s3Key,
                    s3Region: s3Config.data?.region || s3Config.region,
                });

                setUploadProgress(prev => ({
                    ...prev,
                    [file.name]: { status: 'completed', progress: 100 }
                }));

            } catch (error) {
                console.error(`Failed to upload ${file.name}:`, error);
                showError(`파일 업로드에 실패했습니다: ${file.name}`);
                setUploadProgress(prev => ({
                    ...prev,
                    [file.name]: { status: 'error', progress: 0, error: error.message }
                }));
            }
        }

        setIsUploading(false);

        // 완료 후 목록 새로고침
        setTimeout(() => {
            if (selectedItem.type === 'part' && selectedItem.id) {
                // 그룹이 선택되어 있으면 그룹 내 파일 목록 새로고침
                if (selectedGroup) {
                    fetchUploadsByGroup(selectedGroup.id);
                } else {
                    // 그룹 목록 새로고침
                    fetchUploadGroups(selectedItem.id);
                }
            }
            setUploadProgress({});
        }, 1500);
    };

    // 다운로드 핸들러
    const handleDownload = async (upload) => {
        try {
            const response = await S3API.getDownloadUrl(upload.id, 'DOWNLOAD');
            const url = response.data?.url || response.url;
            window.open(url, '_blank');
            // 다운로드 수 반영을 위해 목록 새로고침
            if (selectedGroup) {
                await fetchUploadsByGroup(selectedGroup.id);
            }
        } catch (err) {
            showError(err.message || '다운로드에 실패했습니다.');
        }
    };

    // 미리보기 핸들러
    const handlePreview = async (upload) => {
        try {
            const response = await S3API.getDownloadUrl(upload.id, 'VIEW');
            const url = response.data?.url || response.url;
            setPreviewFile(upload);
            setPreviewUrl(url);
            setShowPreviewModal(true);
        } catch (err) {
            showError(err.message || '미리보기에 실패했습니다.');
        }
    };

    // 업로드 삭제 핸들러
    const handleDeleteUpload = (uploadId) => {
        setConfirmModal({
            isOpen: true,
            title: '파일 삭제',
            message: '이 파일을 삭제하시겠습니까?',
            type: 'danger',
            confirmText: '삭제',
            cancelText: '취소',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await UploadsAPI.deleteUpload(uploadId);
                    success('파일이 삭제되었습니다.');
                    // 그룹이 선택되어 있으면 그룹 내 파일 목록 새로고침
                    if (selectedGroup) {
                        await fetchUploadsByGroup(selectedGroup.id);
                        // 그룹 목록 갱신하여 fileCount 동기화
                        if (selectedItem.type === 'part' && selectedItem.id) {
                            await fetchUploadGroups(selectedItem.id);
                        }
                    }
                } catch (err) {
                    showError(err.message || '삭제에 실패했습니다.');
                }
            }
        });
    };

    // 그룹 수정 모달 열기
    const handleEditGroup = (group, e) => {
        e.stopPropagation(); // 카드 클릭 이벤트 방지
        setEditingGroup(group);
        setGroupEditForm({
            title: group.title || '',
            description: group.description || '',
        });
        setShowGroupEditModal(true);
    };

    // 그룹 수정 저장
    const handleGroupEditSave = async () => {
        if (!groupEditForm.title.trim()) {
            warning('자료명을 입력해주세요.');
            return;
        }
        try {
            await UploadGroupsAPI.update(editingGroup.id, {
                title: groupEditForm.title.trim(),
                description: groupEditForm.description.trim() || null,
            });
            success('자료 그룹이 수정되었습니다.');
            setShowGroupEditModal(false);
            setEditingGroup(null);
            // 그룹 목록 새로고침
            if (selectedItem.type === 'part' && selectedItem.id) {
                await fetchUploadGroups(selectedItem.id);
            }
        } catch (err) {
            showError(err.message || '수정에 실패했습니다.');
        }
    };

    // 그룹 삭제 핸들러
    const handleDeleteGroup = (group, e) => {
        e.stopPropagation(); // 카드 클릭 이벤트 방지
        const message = group.fileCount > 0
            ? `"${group.title}" 그룹을 삭제하시겠습니까?\n이 그룹에 포함된 ${group.fileCount}개의 파일도 모두 삭제됩니다.`
            : `"${group.title}" 그룹을 삭제하시겠습니까?`;
        setConfirmModal({
            isOpen: true,
            title: '그룹 삭제',
            message,
            type: 'danger',
            confirmText: '삭제',
            cancelText: '취소',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await UploadGroupsAPI.delete(group.id);
                    success('자료 그룹이 삭제되었습니다.');
                    // 그룹 목록 새로고침
                    if (selectedItem.type === 'part' && selectedItem.id) {
                        await fetchUploadGroups(selectedItem.id);
                    }
                } catch (err) {
                    showError(err.message || '삭제에 실패했습니다.');
                }
            }
        });
    };

    // 상태 라벨
    const getStatusLabel = (status) => {
        const statusLabels = {
            'PENDING': '가입 대기',
            'APPROVED': '승인됨',
            'REJECTED': '거절됨',
            'SUSPENDED': '정지됨',
            'pending': '가입 대기',
            'approved': '승인됨',
            'rejected': '거절됨',
            'suspended': '정지됨',
        };
        return statusLabels[status] || status;
    };

    // 상태 클래스
    const getStatusClass = (status) => {
        const statusClasses = {
            'PENDING': 'status-pending',
            'APPROVED': 'status-approved',
            'REJECTED': 'status-rejected',
            'SUSPENDED': 'status-suspended',
            'pending': 'status-pending',
            'approved': 'status-approved',
            'rejected': 'status-rejected',
            'suspended': 'status-suspended',
        };
        return statusClasses[status] || '';
    };

    // 역할 라벨
    const getRoleLabel = (role) => {
        if (!role) return '-';
        const roleLabels = {
            super_admin: '최고관리자',
            admin: '관리자',
            sales_master: '영업마스터',
            sales_branch_manager: '지점장',
            sales_general: '일반영업사원',
            SUPER_ADMIN: '최고관리자',
            ADMIN: '관리자',
            SALES_MASTER: '영업마스터',
            SALES_BRANCH_MANAGER: '지점장',
            SALES_GENERAL: '일반영업사원',
        };
        // 콤마로 구분된 여러 역할 처리
        if (role.includes(',')) {
            return role.split(',').map(r => roleLabels[r.trim()] || r.trim()).join(', ');
        }
        return roleLabels[role] || role;
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <div className="admin-page">
            <Navigation />
            <div className="admin-container">
                <div className="admin-header">
                    <h1 className="admin-title">관리자 페이지</h1>
                    <p className="admin-subtitle">사용자, 역할 및 권한을 관리합니다</p>
                </div>

                {/* 탭 네비게이션 */}
                <div className="admin-tabs">
                    <button
                        className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => setActiveTab('users')}
                    >
                        사용자 관리
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'roles' ? 'active' : ''}`}
                        onClick={() => setActiveTab('roles')}
                    >
                        역할 관리
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'permissions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('permissions')}
                    >
                        권한 관리
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'sections' ? 'active' : ''}`}
                        onClick={() => setActiveTab('sections')}
                    >
                        섹션 관리
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'files' ? 'active' : ''}`}
                        onClick={() => setActiveTab('files')}
                    >
                        파일 관리
                    </button>
                </div>

                {/* 사용자 관리 탭 */}
                {activeTab === 'users' && (
                    <div className="admin-content">
                        <div className="content-toolbar">
                            <div className="filter-group">
                                <label>상태 필터:</label>
                                <select
                                    value={userFilter}
                                    onChange={(e) => setUserFilter(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">전체</option>
                                    <option value="PENDING">가입 대기</option>
                                    <option value="APPROVED">승인됨</option>
                                    <option value="REJECTED">거절됨</option>
                                    <option value="SUSPENDED">정지됨</option>
                                </select>
                            </div>
                            <button className="refresh-btn" onClick={fetchUsers}>
                                새로고침
                            </button>
                        </div>

                        {usersLoading ? (
                            <div className="loading">로딩 중...</div>
                        ) : (
                            <div className="table-container">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>이름</th>
                                            <th>이메일</th>
                                            <th>소속</th>
                                            <th>본부</th>
                                            <th>역할</th>
                                            <th>상태</th>
                                            <th>가입일</th>
                                            <th>액션</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((u) => (
                                            <tr key={u.id}>
                                                <td>{u.name}</td>
                                                <td>{u.email}</td>
                                                <td>{u.affiliationName || '-'}</td>
                                                <td>{u.headquartersName || '-'}</td>
                                                <td>
                                                    <span className="role-badge">
                                                        {u.roleDisplayNames || getRoleLabel(u.roles)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${getStatusClass(u.status)}`}>
                                                        {getStatusLabel(u.status)}
                                                    </span>
                                                </td>
                                                <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}</td>
                                                <td>
                                                    <div className="action-buttons">
                                                        {(u.status === 'PENDING' || u.status === 'pending') && (
                                                            <>
                                                                <button
                                                                    className="action-btn approve"
                                                                    onClick={() => handleApprove(u.id)}
                                                                >
                                                                    승인
                                                                </button>
                                                                <button
                                                                    className="action-btn reject"
                                                                    onClick={() => {
                                                                        setRejectUserId(u.id);
                                                                        setShowRejectModal(true);
                                                                    }}
                                                                >
                                                                    반려
                                                                </button>
                                                            </>
                                                        )}
                                                        <button
                                                            className="action-btn edit"
                                                            onClick={() => {
                                                                setEditingUser({ ...u });
                                                                setShowUserEditModal(true);
                                                            }}
                                                        >
                                                            수정
                                                        </button>
                                                        <button
                                                            className="action-btn role"
                                                            onClick={() => {
                                                                setAssigningUser(u);
                                                                setPendingRoleId(u.roleId);
                                                                setShowRoleAssignModal(true);
                                                            }}
                                                        >
                                                            역할
                                                        </button>
                                                        <button
                                                            className="action-btn delete"
                                                            onClick={() => handleDeleteUser(u.id)}
                                                        >
                                                            삭제
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {users.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="empty-message">
                                                    사용자가 없습니다.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* 역할 관리 탭 */}
                {activeTab === 'roles' && (
                    <div className="admin-content">
                        <div className="content-toolbar">
                            <button
                                className="add-btn"
                                onClick={() => {
                                    setEditingRole(null);
                                    setRoleForm({ name: '', description: '', level: 0 });
                                    setShowRoleModal(true);
                                }}
                            >
                                + 새 역할 추가
                            </button>
                            <button className="refresh-btn" onClick={fetchRoles}>
                                새로고침
                            </button>
                        </div>

                        {rolesLoading ? (
                            <div className="loading">로딩 중...</div>
                        ) : (
                            <div className="table-container">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>역할명</th>
                                            <th>설명</th>
                                            <th>권한 레벨</th>
                                            <th>액션</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {roles.map((role) => (
                                            <tr key={role.id}>
                                                <td>
                                                    <span className="role-name">{role.displayName || role.name}</span>
                                                </td>
                                                <td>{role.description || '-'}</td>
                                                <td>{role.level}</td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            className="action-btn edit"
                                                            onClick={() => {
                                                                setEditingRole(role);
                                                                setRoleForm({
                                                                    name: role.name,
                                                                    description: role.description || '',
                                                                    level: role.level || 0,
                                                                });
                                                                setShowRoleModal(true);
                                                            }}
                                                        >
                                                            수정
                                                        </button>
                                                        <button
                                                            className="action-btn delete"
                                                            onClick={() => handleRoleDelete(role.id)}
                                                        >
                                                            삭제
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {roles.length === 0 && (
                                            <tr>
                                                <td colSpan="4" className="empty-message">
                                                    역할이 없습니다.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* 권한 관리 탭 */}
                {activeTab === 'permissions' && (
                    <div className="admin-content permissions-layout">
                        {/* 좌측: 역할 목록 사이드바 */}
                        <div className="roles-sidebar">
                            <div className="roles-sidebar-header">역할 목록</div>
                            {roles.map((role) => (
                                <div
                                    key={role.id}
                                    className={`role-card ${selectedRoleId === role.id ? 'active' : ''}`}
                                    onClick={() => setSelectedRoleId(role.id)}
                                >
                                    <div className="role-card-name">
                                        {role.displayName || role.name}
                                    </div>
                                    <div className="role-card-level">
                                        레벨: {role.level}
                                    </div>
                                </div>
                            ))}
                            {roles.length === 0 && (
                                <div className="empty-message">역할이 없습니다.</div>
                            )}
                        </div>

                        {/* 우측: 권한 설정 영역 */}
                        <div className="permissions-main">
                            {selectedRoleId ? (
                                <>
                                    <div className="permissions-header">
                                        <h3 className="permissions-title">
                                            {roles.find(r => r.id === selectedRoleId)?.displayName || roles.find(r => r.id === selectedRoleId)?.name} 권한 설정
                                        </h3>
                                        <button
                                            className="refresh-btn"
                                            onClick={() => fetchRolePermissions(selectedRoleId)}
                                            title="권한 새로고침"
                                        >
                                            새로고침
                                        </button>
                                    </div>

                                    {permissionsLoading ? (
                                        <div className="loading">로딩 중...</div>
                                    ) : (
                                        <div className="permissions-container">
                                            {menuStructure.map((section) => (
                                                <div key={section.id} className="permission-section">
                                                    <div className="section-header">
                                                        <h3 className="section-title">{section.name}</h3>
                                                        <div className="permission-checkboxes">
                                                            <label className="checkbox-label">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={permissions[`section_${section.id}`]?.canAccess || false}
                                                                    onChange={(e) =>
                                                                        handlePermissionChange('section', section.id, 'canAccess', e.target.checked)
                                                                    }
                                                                />
                                                                접근
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {section.categories?.map((category) => (
                                                        <div key={category.id} className="permission-category">
                                                            <>
                                                                <div className="category-header">
                                                                    <h4 className="category-title">{category.name}</h4>
                                                                    <div className="permission-checkboxes">
                                                                        <label className="checkbox-label checkbox-all">
                                                                            <input type="checkbox" checked={isCategoryAllChecked(category)}
                                                                                disabled={!permissions[`section_${section.id}`]?.canAccess}
                                                                                onChange={(e) => handleCategoryAllChange(category, e.target.checked)} />
                                                                            전체
                                                                        </label>
                                                                    </div>
                                                                </div>

                                                                {category.parts?.map((part) => (
                                                                    <div key={part.id} className="permission-part">
                                                                        <span className="part-name">{part.name}</span>
                                                                        <div className="permission-checkboxes">
                                                                            <label className="checkbox-label">
                                                                                <input type="checkbox" checked={permissions[`part_${part.id}`]?.canView || false}
                                                                                    disabled={!permissions[`section_${section.id}`]?.canAccess}
                                                                                    onChange={(e) => handlePermissionChange('part', part.id, 'canView', e.target.checked)} />
                                                                                읽기
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                            {menuStructure.length === 0 && (
                                                <div className="empty-message">콘텐츠 구조가 없습니다.</div>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="empty-message">좌측에서 역할을 선택해주세요.</div>
                            )}
                        </div>
                    </div>
                )}

                {/* 섹션 관리 탭 */}
                {activeTab === 'sections' && (
                    <div className="admin-content section-management-layout">
                        {/* 좌측: 콘텐츠 트리 */}
                        <div className="section-mgmt-sidebar">
                            <div className="accordion-header">
                                <h3>콘텐츠 구조</h3>
                            </div>
                            {sectionMgmtLoading ? (
                                <div className="loading-message">로딩 중...</div>
                            ) : (
                                <div className="section-mgmt-tree">
                                    {sectionMgmtData.map(section => (
                                        <div key={section.id} className="section-mgmt-section">
                                            <div
                                                className="section-mgmt-section-header"
                                                onClick={() => toggleMgmtSection(section.id)}
                                            >
                                                <span className="section-mgmt-toggle">
                                                    {expandedMgmtSections[section.id] ? '▼' : '▶'}
                                                </span>
                                                <span className="section-mgmt-section-name">{section.name}</span>
                                                <span className="section-mgmt-count">({section.categories?.length || 0})</span>
                                            </div>

                                            {expandedMgmtSections[section.id] && (
                                                <div className="section-mgmt-categories">
                                                    {section.categories?.map(cat => (
                                                        <div key={cat.id} className="section-mgmt-category-group">
                                                            <div
                                                                className={`section-mgmt-category-item ${
                                                                    selectedMgmtItem?.type === 'category' && selectedMgmtItem?.data?.id === cat.id ? 'active' : ''
                                                                }`}
                                                                onClick={() => handleEditCategory(cat)}
                                                            >
                                                                <span className="section-mgmt-category-name">{cat.name}</span>
                                                                <span className="section-mgmt-count">({cat.parts?.length || 0})</span>
                                                                <button
                                                                    className="section-mgmt-expand-btn"
                                                                    onClick={(e) => { e.stopPropagation(); toggleMgmtCategory(cat.id); }}
                                                                >
                                                                    {expandedMgmtCategories[cat.id] ? '▼' : '▶'}
                                                                </button>
                                                            </div>

                                                            {expandedMgmtCategories[cat.id] && (
                                                                <div className="section-mgmt-parts">
                                                                    {cat.parts?.map(part => (
                                                                        <div
                                                                            key={part.id}
                                                                            className={`section-mgmt-part-item ${
                                                                                selectedMgmtItem?.type === 'part' && selectedMgmtItem?.data?.id === part.id ? 'active' : ''
                                                                            }`}
                                                                            onClick={() => handleEditPart(part)}
                                                                        >
                                                                            <span className="section-mgmt-part-name">{part.name}</span>
                                                                            <span className="section-mgmt-part-type">{part.partType}</span>
                                                                        </div>
                                                                    ))}
                                                                    <button
                                                                        className="section-mgmt-add-btn"
                                                                        onClick={() => handleCreatePart(cat.id)}
                                                                    >
                                                                        + 파트 추가
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                    <button
                                                        className="section-mgmt-add-btn"
                                                        onClick={() => handleCreateCategory(section.id)}
                                                    >
                                                        + 카테고리 추가
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 우측: 편집 폼 */}
                        <div className="section-mgmt-main">
                            {editMode === null ? (
                                <div className="empty-message">좌측에서 카테고리 또는 파트를 선택하세요.</div>
                            ) : editMode?.includes('category') ? (
                                <div className="section-mgmt-form">
                                    <h3>{editMode === 'create-category' ? '카테고리 추가' : '카테고리 수정'}</h3>
                                    <div className="form-group">
                                        <label>이름 <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            value={editForm.name || ''}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="카테고리 이름을 입력하세요"
                                            maxLength={100}
                                        />
                                    </div>
                                    {editMode === 'edit-category' && (
                                        <div className="form-group">
                                            <label>표시 순서</label>
                                            <input
                                                type="number"
                                                value={editForm.displayOrder ?? 0}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                                                min={0}
                                            />
                                        </div>
                                    )}
                                    <div className="section-mgmt-form-actions">
                                        <button className="btn-save" onClick={handleSaveCategory}>저장</button>
                                        <button className="btn-cancel" onClick={() => { setEditMode(null); setEditForm({}); setSelectedMgmtItem(null); }}>취소</button>
                                        {editMode === 'edit-category' && (
                                            <button className="btn-danger" onClick={() => handleDeleteCategory(selectedMgmtItem.data)}>삭제</button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="section-mgmt-form">
                                    <h3>{editMode === 'create-part' ? '파트 추가' : '파트 수정'}</h3>
                                    <div className="form-group">
                                        <label>이름 <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            value={editForm.name || ''}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="파트 이름을 입력하세요"
                                            maxLength={100}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>파트 유형 <span className="required">*</span></label>
                                        <select
                                            value={editForm.partType || 'LINK'}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, partType: e.target.value }))}
                                        >
                                            <option value="LINK">사이트 이동</option>
                                            <option value="GALLERY">PDF / 동영상</option>
                                        </select>
                                    </div>
                                    {(editForm.partType || 'LINK') === 'LINK' && (
                                        <div className="form-group">
                                            <label>외부 URL <span className="required">*</span></label>
                                            <input
                                                type="text"
                                                value={editForm.externalUrl || ''}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, externalUrl: e.target.value }))}
                                                placeholder="https://example.com"
                                            />
                                        </div>
                                    )}
                                    <div className="form-group">
                                        <label>버튼 텍스트 <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            value={editForm.buttonText || ''}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, buttonText: e.target.value }))}
                                            placeholder="바로가기"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>아이콘 <span className="required">*</span></label>
                                        <LucideIconPicker
                                            value={editForm.icon || null}
                                            onChange={(icon) => setEditForm(prev => ({ ...prev, icon: icon || '' }))}
                                        />
                                    </div>
                                    {editMode === 'edit-part' && (
                                        <div className="form-group">
                                            <label>표시 순서</label>
                                            <input
                                                type="number"
                                                value={editForm.displayOrder ?? 0}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                                                min={0}
                                            />
                                        </div>
                                    )}
                                    <div className="section-mgmt-form-actions">
                                        <button className="btn-save" onClick={handleSavePart}>저장</button>
                                        <button className="btn-cancel" onClick={() => { setEditMode(null); setEditForm({}); setSelectedMgmtItem(null); }}>취소</button>
                                        {editMode === 'edit-part' && (
                                            <button className="btn-danger" onClick={() => handleDeletePart(selectedMgmtItem.data)}>삭제</button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 파일 관리 탭 */}
                {activeTab === 'files' && (
                    <div className="admin-content file-management-layout">
                        {/* 왼쪽: 아코디언 메뉴 */}
                        <div className="file-accordion-sidebar">
                            <div className="accordion-header">
                                <h3>콘텐츠 구조</h3>
                            </div>
                            <div className="accordion-content">
                                {menuStructure.map((section) => (
                                    <div key={section.id} className="accordion-section">
                                        {/* 섹션 헤더 */}
                                        <div
                                            className={`accordion-section-header ${selectedItem.type === 'section' && selectedItem.id === section.id ? 'active' : ''}`}
                                            onClick={() => {
                                                // 카테고리가 없으면 섹션 자체를 선택
                                                if (!section.categories || section.categories.length === 0) {
                                                    handleSelectItem('section', section.id, section.name);
                                                } else {
                                                    toggleSection(section.id);
                                                }
                                            }}
                                        >
                                            <span className="accordion-icon">
                                                {section.categories && section.categories.length > 0
                                                    ? (expandedSections[section.id] ? '▼' : '▶')
                                                    : '○'}
                                            </span>
                                            <span className="accordion-title">{section.name}</span>
                                        </div>

                                        {/* 카테고리 목록 */}
                                        {expandedSections[section.id] && section.categories && (
                                            <div className="accordion-categories">
                                                {section.categories.map((category) => (
                                                    <div key={category.id} className="accordion-category">
                                                        {/* 카테고리 헤더 */}
                                                        <div
                                                            className={`accordion-category-header ${selectedItem.type === 'category' && selectedItem.id === category.id ? 'active' : ''}`}
                                                            onClick={() => {
                                                                // 파트가 없으면 카테고리 자체를 선택
                                                                if (!category.parts || category.parts.length === 0) {
                                                                    handleSelectItem('category', category.id, category.name);
                                                                } else {
                                                                    toggleCategory(category.id);
                                                                }
                                                            }}
                                                        >
                                                            <span className="accordion-icon">
                                                                {category.parts && category.parts.length > 0
                                                                    ? (expandedCategories[category.id] ? '▼' : '▶')
                                                                    : '○'}
                                                            </span>
                                                            <span className="accordion-title">{category.name}</span>
                                                        </div>

                                                        {/* 파트 목록 */}
                                                        {expandedCategories[category.id] && category.parts && (
                                                            <div className="accordion-parts">
                                                                {category.parts.map((part) => (
                                                                    <div
                                                                        key={part.id}
                                                                        className={`accordion-part ${selectedItem.type === 'part' && selectedItem.id === part.id ? 'active' : ''}`}
                                                                        onClick={() => handleSelectItem('part', part.id, part.name)}
                                                                    >
                                                                        <span className="accordion-icon">📄</span>
                                                                        <span className="accordion-title">{part.name}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {menuStructure.length === 0 && (
                                    <div className="accordion-empty">콘텐츠가 없습니다.</div>
                                )}
                            </div>
                        </div>

                        {/* 오른쪽: 파일 목록 영역 */}
                        <div className="file-list-area">
                            {/* 상단: 선택된 항목 정보 및 업로드 버튼 */}
                            <div className="file-list-header">
                                <div className="selected-item-info">
                                    {fileViewMode === 'files' && selectedGroup ? (
                                        <>
                                            <button
                                                className="back-btn"
                                                onClick={handleBackToGroups}
                                            >
                                                ◀ 뒤로
                                            </button>
                                            <span className="selected-item-name">{selectedGroup.title}</span>
                                            <span className="file-count">({uploads.length}개 파일)</span>
                                        </>
                                    ) : selectedItem.id ? (
                                        <>
                                            <span className="selected-item-type">
                                                {selectedItem.type === 'section' ? '섹션' : selectedItem.type === 'category' ? '카테고리' : '파트'}
                                            </span>
                                            <span className="selected-item-name">{selectedItem.name}</span>
                                            {selectedItem.type === 'part' && (
                                                <span className="file-count">({uploadGroups.length}개 자료)</span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="selected-item-placeholder">왼쪽 메뉴에서 항목을 선택하세요</span>
                                    )}
                                </div>
                                <div className="file-list-actions">
                                    {selectedItem.type === 'part' && fileViewMode === 'groups' && (
                                        <button
                                            className="add-btn"
                                            onClick={() => {
                                                setUploadFiles([]);
                                                setUploadTitle('');
                                                setUploadModalMode('createGroup');
                                                setShowUploadModal(true);
                                            }}
                                        >
                                            + 파일 그룹 생성
                                        </button>
                                    )}
                                    {selectedItem.type === 'part' && fileViewMode === 'files' && selectedGroup && (
                                        <button
                                            className="add-btn"
                                            onClick={() => {
                                                setUploadFiles([]);
                                                setUploadTitle(selectedGroup.title);
                                                setUploadModalMode('addFiles');
                                                setShowUploadModal(true);
                                            }}
                                        >
                                            + 파일 추가
                                        </button>
                                    )}
                                    {selectedItem.type === 'part' && (
                                        <button
                                            className="refresh-btn"
                                            onClick={() => {
                                                if (fileViewMode === 'files' && selectedGroup) {
                                                    fetchUploadsByGroup(selectedGroup.id);
                                                } else {
                                                    fetchUploadGroups(selectedItem.id);
                                                }
                                            }}
                                        >
                                            새로고침
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* 업로드 진행률 표시 */}
                            {Object.keys(uploadProgress).length > 0 && (
                                <div className="upload-progress-container">
                                    {Object.entries(uploadProgress).map(([fileName, info]) => (
                                        <div key={fileName} className="upload-progress-item">
                                            <div className="upload-progress-info">
                                                <span className="upload-progress-filename">{fileName}</span>
                                                <div className="upload-progress-bar">
                                                    <div
                                                        className={`upload-progress-fill ${info.status === 'error' ? 'error' : ''}`}
                                                        style={{ width: `${info.progress}%` }}
                                                    />
                                                </div>
                                            </div>
                                            <span className="upload-progress-percent">
                                                {info.status === 'error' ? '오류' : info.status === 'completed' ? '완료' : `${info.progress}%`}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* 자료 그룹 목록 또는 파일 목록 */}
                            {selectedItem.type === 'part' ? (
                                fileViewMode === 'groups' ? (
                                    // 자료 그룹 목록
                                    groupsLoading ? (
                                        <div className="loading">로딩 중...</div>
                                    ) : (
                                        <div className="upload-groups-container">
                                            {uploadGroups.length > 0 ? (
                                                <div className="upload-groups-grid">
                                                    {uploadGroups.map((group) => (
                                                        <div
                                                            key={group.id}
                                                            className="upload-group-card"
                                                            onClick={() => handleSelectGroup(group)}
                                                        >
                                                            <div className="group-icon">📁</div>
                                                            <div className="group-info">
                                                                <span className="group-title">{group.title}</span>
                                                                <span className="group-file-count">{group.fileCount || 0}개 파일</span>
                                                            </div>
                                                            <div className="group-actions">
                                                                <button
                                                                    className="group-action-btn edit"
                                                                    onClick={(e) => handleEditGroup(group, e)}
                                                                    title="수정"
                                                                >
                                                                    ✏️
                                                                </button>
                                                                <button
                                                                    className="group-action-btn delete"
                                                                    onClick={(e) => handleDeleteGroup(group, e)}
                                                                    title="삭제"
                                                                >
                                                                    🗑️
                                                                </button>
                                                            </div>
                                                            <div className="group-arrow">▶</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="empty-message">
                                                    등록된 자료가 없습니다.<br />
                                                    '새 자료 추가' 버튼을 클릭하여 파일을 업로드하세요.
                                                </div>
                                            )}
                                        </div>
                                    )
                                ) : (
                                    // 파일 목록
                                    uploadsLoading ? (
                                        <div className="loading">로딩 중...</div>
                                    ) : (
                                        <div className="table-container">
                                            <table className="admin-table file-table">
                                                <thead>
                                                    <tr>
                                                        <th className="th-icon"></th>
                                                        <th className="th-order">순서</th>
                                                        <th className="th-title">제목</th>
                                                        <th className="th-uploader">업로더</th>
                                                        <th className="th-date">업로드일</th>
                                                        <th className="th-download">다운로드</th>
                                                        <th className="th-action">액션</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {uploads.map((upload, index) => (
                                                        <tr
                                                            key={upload.id}
                                                            data-upload-id={upload.id}
                                                            className={`${draggedUploadIndex === index ? 'dragging' : ''} ${(upload.fileType === 'PDF' || upload.fileType === 'IMAGE') ? 'previewable' : ''}`}
                                                            draggable
                                                            onDragStart={(e) => handleUploadDragStart(e, index)}
                                                            onDragOver={handleUploadDragOver}
                                                            onDrop={(e) => handleUploadDrop(e, index)}
                                                            onDragEnd={handleUploadDragEnd}
                                                        >
                                                            <td className="td-icon">
                                                                <span className="drag-handle">☰</span>
                                                            </td>
                                                            <td className="td-order">
                                                                <span className="order-number">{index + 1}</span>
                                                            </td>
                                                            <td className="td-title">
                                                                <div className="file-name-cell centered">
                                                                    <div className="file-meta">
                                                                        <span className="file-name">{upload.originalName}</span>
                                                                        <span className="file-size">{formatFileSize(upload.fileSize)}</span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="td-uploader">{upload.uploaderName || '-'}</td>
                                                            <td className="td-date">{upload.createdAt ? new Date(upload.createdAt).toLocaleDateString() : '-'}</td>
                                                            <td className="td-download">{upload.downloadCount || 0}회</td>
                                                            <td className="td-action">
                                                                <div className="action-buttons">
                                                                    <button
                                                                        className="action-btn approve"
                                                                        onClick={() => handleDownload(upload)}
                                                                    >
                                                                        다운로드
                                                                    </button>
                                                                    <button
                                                                        className="action-btn delete"
                                                                        onClick={() => handleDeleteUpload(upload.id)}
                                                                    >
                                                                        삭제
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {uploads.length === 0 && (
                                                        <tr>
                                                            <td colSpan="7" className="empty-message">
                                                                이 자료에 파일이 없습니다.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                )
                            ) : selectedItem.id ? (
                                <div className="empty-message">
                                    파일 업로드는 파트 단위에서만 가능합니다.<br />
                                    하위 파트를 선택해주세요.
                                </div>
                            ) : (
                                <div className="empty-message">
                                    왼쪽 메뉴에서 파트를 선택하면 자료 목록이 표시됩니다.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* 반려 모달 */}
            {showRejectModal && (
                <div className="admin-modal-overlay" onClick={() => setShowRejectModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2 className="admin-modal-title">회원 반려</h2>
                            <button
                                className="admin-modal-close"
                                onClick={() => setShowRejectModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-radio-grid">
                                {rejectReasonOptions.map((option) => (
                                    <label key={option.value} className="admin-radio-option">
                                        <input
                                            type="radio"
                                            name="rejectReason"
                                            value={option.value}
                                            checked={rejectReasonType === option.value}
                                            onChange={(e) => setRejectReasonType(e.target.value)}
                                        />
                                        <span className="admin-radio-custom"></span>
                                        <span className="admin-radio-label">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            <textarea
                                className="admin-modal-textarea"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="상세 사유를 입력해주세요"
                                rows={4}
                            />
                            <p className="admin-modal-notice">반려 시 해당 사용자에게 사유가 전달됩니다.</p>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-modal-btn confirm" onClick={handleReject}>
                                반려하기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 회원 수정 모달 */}
            {showUserEditModal && editingUser && (
                <div className="admin-modal-overlay" onClick={() => setShowUserEditModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2 className="admin-modal-title">회원 정보 수정</h2>
                            <button
                                className="admin-modal-close"
                                onClick={() => setShowUserEditModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form-group">
                                <label className="admin-modal-label">이름</label>
                                <input
                                    type="text"
                                    className="admin-modal-input"
                                    value={editingUser.name}
                                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                                    placeholder="이름을 입력하세요"
                                />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-modal-label">소속</label>
                                <select
                                    className="admin-modal-select"
                                    value={editingUser.affiliationId || ''}
                                    onChange={(e) => setEditingUser({ ...editingUser, affiliationId: e.target.value })}
                                >
                                    <option value="">소속 선택</option>
                                    {affiliations.map((aff) => (
                                        <option key={aff.id} value={aff.id}>{aff.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-modal-label">본부</label>
                                <select
                                    className="admin-modal-select"
                                    value={editingUser.headquartersId || ''}
                                    onChange={(e) => setEditingUser({ ...editingUser, headquartersId: e.target.value })}
                                >
                                    <option value="">본부 선택</option>
                                    {headquarters.map((hq) => (
                                        <option key={hq.id} value={hq.id}>{hq.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-modal-btn blue" onClick={handleUserEdit}>
                                저장하기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 역할 생성/수정 모달 */}
            {showRoleModal && (
                <div className="admin-modal-overlay" onClick={() => setShowRoleModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2 className="admin-modal-title">{editingRole ? '역할 수정' : '새 역할 추가'}</h2>
                            <button
                                className="admin-modal-close"
                                onClick={() => setShowRoleModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form-group">
                                <label className="admin-modal-label">역할명</label>
                                <input
                                    type="text"
                                    className="admin-modal-input"
                                    value={roleForm.name}
                                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                                    placeholder="역할명을 입력하세요"
                                />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-modal-label">설명</label>
                                <textarea
                                    className="admin-modal-textarea"
                                    value={roleForm.description}
                                    onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                                    placeholder="역할 설명을 입력하세요"
                                    rows={3}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-modal-label">권한 레벨</label>
                                <input
                                    type="number"
                                    className="admin-modal-input"
                                    value={roleForm.level}
                                    onChange={(e) => setRoleForm({ ...roleForm, level: parseInt(e.target.value) || 0 })}
                                    placeholder="숫자가 높을수록 상위 권한"
                                />
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-modal-btn purple" onClick={handleRoleSave}>
                                {editingRole ? '수정하기' : '추가하기'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 역할 부여 모달 */}
            {showRoleAssignModal && assigningUser && (
                <div className="admin-modal-overlay" onClick={() => setShowRoleAssignModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2 className="admin-modal-title">역할 관리</h2>
                            <button
                                className="admin-modal-close"
                                onClick={() => setShowRoleAssignModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="admin-modal-body">
                            <p className="admin-current-role">
                                <strong>{assigningUser.name}</strong>님의 현재 역할: <strong>{getRoleLabel(assigningUser.roles)}</strong>
                            </p>
                            <label className="admin-modal-label">새 역할 선택</label>
                            <div className="admin-role-list">
                                {roles.map((role) => (
                                    <button
                                        key={role.id}
                                        className={`admin-role-item ${pendingRoleId === role.id ? 'selected' : ''} ${assigningUser.roleId === role.id ? 'current' : ''}`}
                                        onClick={() => setPendingRoleId(role.id)}
                                    >
                                        {role.displayName || role.name}
                                    </button>
                                ))}
                            </div>
                            {assigningUser.roleId && (
                                <button
                                    className="admin-revoke-btn"
                                    onClick={() => handleRevokeRole(assigningUser.id, assigningUser.roleId)}
                                >
                                    역할 해제
                                </button>
                            )}
                            <p className="admin-modal-notice">역할 선택 후 완료 버튼을 눌러 저장하세요.</p>
                        </div>
                        <div className="admin-modal-footer">
                            <button
                                className="admin-modal-btn purple"
                                onClick={handleConfirmRoleChange}
                                disabled={pendingRoleId === assigningUser.roleId}
                            >
                                완료
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 파일 미리보기 모달 */}
            {showPreviewModal && previewFile && (
                <div className="admin-modal-overlay" onClick={() => setShowPreviewModal(false)}>
                    <div className="admin-modal preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2 className="admin-modal-title">{previewFile.title}</h2>
                            <button
                                className="admin-modal-close"
                                onClick={() => setShowPreviewModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="preview-modal-body">
                            {previewFile.fileType === 'PDF' ? (
                                <iframe
                                    src={previewUrl}
                                    className="preview-pdf"
                                    title="PDF Preview"
                                />
                            ) : (
                                <img
                                    src={previewUrl}
                                    alt={previewFile.title}
                                    className="preview-image"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 파일 업로드 모달 */}
            {showUploadModal && (
                <div className="admin-modal-overlay" onClick={handleUploadCancel}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2 className="admin-modal-title">
                                {uploadModalMode === 'createGroup' ? '파일 그룹 생성' : '파일 업로드'}
                            </h2>
                            <button
                                className="admin-modal-close"
                                onClick={handleUploadCancel}
                            >
                                ×
                            </button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form-group">
                                <label className="admin-modal-label">
                                    {uploadModalMode === 'createGroup' ? '그룹 이름 *' : '제목 *'}
                                </label>
                                <input
                                    type="text"
                                    className="admin-modal-input"
                                    value={uploadTitle}
                                    onChange={(e) => setUploadTitle(e.target.value)}
                                    placeholder={uploadModalMode === 'createGroup' ? '그룹 이름을 입력하세요' : '파일 제목을 입력하세요'}
                                    autoFocus
                                />
                            </div>
                            {uploadModalMode === 'addFiles' && (
                                <div className="admin-form-group">
                                    <label className="admin-modal-label">파일 선택 *</label>
                                    <div
                                        className={`file-drop-zone ${isDragOver ? 'drag-over' : ''}`}
                                        onClick={handleFileButtonClick}
                                        onDragOver={handleDropZoneDragOver}
                                        onDragLeave={handleDropZoneDragLeave}
                                        onDrop={handleDropZoneDrop}
                                    >
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            multiple
                                            accept=".pdf,.mp4,.webm,.mov"
                                            style={{ display: 'none' }}
                                            onChange={handleFileSelect}
                                        />
                                        <div className="drop-zone-icon">📂</div>
                                        <p className="drop-zone-text">파일을 드래그하여 놓거나 클릭하여 선택하세요</p>
                                        <p className="drop-zone-info">허용 형식: PDF, 동영상(MP4, WebM, MOV) · 용량 제한: PDF 500MB / 동영상 700MB</p>
                                    </div>
                                    {uploadFiles.length > 0 && (
                                        <div className="upload-file-list">
                                            <p className="upload-file-hint">드래그하여 파일 순서를 변경할 수 있습니다.</p>
                                            {uploadFiles.map((file, index) => (
                                                <div
                                                    key={index}
                                                    className={`upload-file-item draggable ${draggedFileIndex === index ? 'dragging' : ''}`}
                                                    draggable
                                                    onDragStart={(e) => handleFileDragStart(e, index)}
                                                    onDragOver={handleFileDragOver}
                                                    onDrop={(e) => handleFileDrop(e, index)}
                                                    onDragEnd={handleFileDragEnd}
                                                >
                                                    <span className="upload-file-drag-handle">☰</span>
                                                    <span className="upload-file-order">{index + 1}</span>
                                                    <span className="upload-file-icon">
                                                        {file.type.includes('pdf') ? '📄' : file.type.startsWith('video/') ? '🎬' : '🖼️'}
                                                    </span>
                                                    <span className="upload-file-name">{file.name}</span>
                                                    <span className="upload-file-size">{formatFileSize(file.size)}</span>
                                                    <button
                                                        type="button"
                                                        className="upload-file-remove-btn"
                                                        onClick={() => {
                                                            setUploadFiles(prev => prev.filter((_, i) => i !== index));
                                                        }}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-modal-btn cancel" onClick={handleUploadCancel}>
                                취소
                            </button>
                            <button className="admin-modal-btn blue" onClick={handleUploadConfirm}>
                                {uploadModalMode === 'createGroup' ? '생성' : '업로드'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 그룹 수정 모달 */}
            {showGroupEditModal && editingGroup && (
                <div className="admin-modal-overlay" onClick={() => setShowGroupEditModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2 className="admin-modal-title">자료 그룹 수정</h2>
                            <button
                                className="admin-modal-close"
                                onClick={() => setShowGroupEditModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form-group">
                                <label className="admin-modal-label">자료명 *</label>
                                <input
                                    type="text"
                                    className="admin-modal-input"
                                    value={groupEditForm.title}
                                    onChange={(e) => setGroupEditForm({ ...groupEditForm, title: e.target.value })}
                                    placeholder="자료명을 입력하세요"
                                    autoFocus
                                />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-modal-label">설명</label>
                                <textarea
                                    className="admin-modal-textarea"
                                    value={groupEditForm.description}
                                    onChange={(e) => setGroupEditForm({ ...groupEditForm, description: e.target.value })}
                                    placeholder="설명을 입력하세요 (선택사항)"
                                    rows={3}
                                />
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-modal-btn cancel" onClick={() => setShowGroupEditModal(false)}>
                                취소
                            </button>
                            <button className="admin-modal-btn blue" onClick={handleGroupEditSave}>
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 커스텀 컨펌 모달 */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.confirmText}
                cancelText={confirmModal.cancelText}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />

            {/* 호버 미리보기 툴팁 */}
            {hoverPreviewUpload && draggedUploadIndex === null && ReactDOM.createPortal(
                <div
                    className="hover-preview-tooltip"
                    style={{
                        position: 'fixed',
                        left: hoverPreviewPositionRef.current.x,
                        top: hoverPreviewPositionRef.current.y,
                        zIndex: 10000,
                    }}
                >
                    <div className="hover-preview-header">
                        <span className="hover-preview-title">{hoverPreviewUpload.originalName}</span>
                        <span className="hover-preview-type">{hoverPreviewUpload.fileType}</span>
                    </div>
                    <div className="hover-preview-content">
                        {hoverPreviewUrl ? (
                            <img
                                src={hoverPreviewUrl}
                                alt={hoverPreviewUpload.originalName}
                                className="hover-preview-image"
                            />
                        ) : (
                            <div className="hover-preview-loading">
                                <div className="loading-spinner"></div>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default AdminPage;
